import React, { ReactElement, useEffect, useState } from 'react'
import { Box } from 'theme-ui'
import { useHotkeys } from 'react-hotkeys-hook'
import FocusLock from 'react-focus-lock'
import { ModalDialogProps } from './ModalDialog.interface'
import ModalDialogHeader from './ModalDialogHeader'
import useOnClickOutside from 'utils/useOnClickOutside'

export const modalDialogMinHeight = 700
export const modalDialogWidth = ['100%', '67.2rem', '67.2rem']

export default function ModalDialog({
  dismiss = (): void => {},
  isOpen,
  onPresentTransitionEnd,
  onDismissTransitionEnd,
  children,
  sx,
  sxOverlay,
  dataTestId = 'modal-dialog-body',
  shouldVibrateOnPresent = false,
  showOverlay = true,
  overlayContent,
  modalDialogRef,
  disableFocusLock,
  title,
  headerButtonType,
  headerStyles,
  headerRef,
  onHeaderButtonClick,
  infoUrl,
}: ModalDialogProps): ReactElement {
  const [isFocusLockDisabled, setIsDisabledFocusLock] = useState(true)

  useOnClickOutside([], () => setIsDisabledFocusLock(true))

  useHotkeys(
    'Tab',
    () => {
      setIsDisabledFocusLock(false)
    },
    [],
  )

  useHotkeys(
    'Escape',
    () => {
      isOpen && dismiss()
    },
    [isOpen],
  )

  const handleTransitionEnd = (): void => {
    if (isOpen && onPresentTransitionEnd) {
      onPresentTransitionEnd()
    } else if (!isOpen && onDismissTransitionEnd) {
      onDismissTransitionEnd()
    }
  }

  useEffect(() => {
    if (shouldVibrateOnPresent && isOpen && window.navigator.vibrate) {
      try {
        window.navigator.vibrate(10)
      } catch (e) {}
    }
    // we only want to trigger this effect when `isOpen` changes, not when
    // `shouldVibrateOnPresent` changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  return (
    <>
      {showOverlay && (
        <Box
          onTransitionEnd={handleTransitionEnd}
          onClick={(): void => dismiss()}
          sx={{
            display: ['none', 'inherit'],
            pt: '10rem',
            visibility: isOpen ? 'inherit' : 'hidden',
            transition: 'opacity 0.2s ease',
            backgroundColor: 'black',
            opacity: isOpen ? '0.9' : '0',
            width: showOverlay ? '100%' : 0,
            height: showOverlay ? '100%' : 0,
            position: 'fixed',
            left: '0px',
            top: '0px',
            zIndex: 'zIndex595',
            ...sxOverlay,
          }}
        >
          {overlayContent}
        </Box>
      )}
      <FocusLock autoFocus={false} disabled={disableFocusLock || isFocusLockDisabled || !isOpen}>
        <Box
          ref={modalDialogRef}
          className={`modal-dialog ${isOpen ? 'transition' : ''}`}
          data-testid={dataTestId}
          aria-live="polite"
          aria-hidden={!isOpen}
          sx={{
            display: ['none', isOpen ? 'block' : 'none'],
            width: modalDialogWidth,
            minHeight: `min(calc(100% - 4.8rem), ${modalDialogMinHeight}px)`,
            maxHeight: 'calc(100% - 4.8rem)',
            willChange: 'transform',
            backgroundColor: 'dialogBodyBg',
            position: 'fixed',
            left: '50%',
            top: '50%',
            border: '1px solid',
            borderColor: 'white15',
            borderRadius: '32px',
            transform: 'translate(-50%, -50%)',
            zIndex: 'zIndex596',
            overflowY: 'auto',
            ...sx,
          }}
          tabIndex={isOpen ? -1 : 0}
        >
          {title && (
            <ModalDialogHeader
              title={title}
              buttonType={headerButtonType}
              headerRef={headerRef}
              onButtonClick={onHeaderButtonClick}
              sx={headerStyles}
              infoUrl={infoUrl}
            />
          )}
          {children}
        </Box>
      </FocusLock>
    </>
  )
}
